/**
 * The wafer map: the single most important picture in the module.
 *
 * A technician does not read etch rate as a number, they read it as a map, and
 * the SHAPE of the map is the diagnosis - a fast edge, a fast centre, and a
 * ring all mean different things. So this renders as a real metrology map:
 * radial field, notch at the bottom for orientation, and the 49-site
 * measurement pattern a tool would actually sample, overlaid on the continuous
 * field it came from.
 *
 * Two encodings, because the data has two different jobs:
 *
 *   'thickness' - magnitude. Sequential single-hue ramp, light = nearly gone.
 *   'deviation' - polarity. Diverging blue/red about a neutral grey midpoint,
 *                 so "on target" reads as absence of colour rather than as a
 *                 colour, and fast-edge vs fast-centre are opposite poles.
 */

import { diverging, FONT_STACK, INK, sequential, SURFACE } from './palette'
import { clamp, mean } from '../core/math'
import { tOr } from '../i18n/i18n'

export type WaferMode = 'thickness' | 'deviation' | 'rate'

export interface WaferMapOptions {
  mode: WaferMode
  /** Per-radius values, index 0 at the wafer centre. */
  values: ArrayLike<number>
  /** Units label for the colour bar. */
  unit: string
  /** Show the 49-point sampling pattern a metrology tool would measure. */
  showSites?: boolean
  /** Optional caption drawn under the map. */
  caption?: string
}

/** 49-site polar sampling pattern, the classic wafer metrology recipe. */
function samplingSites(): Array<{ r: number; theta: number }> {
  const sites: Array<{ r: number; theta: number }> = [{ r: 0, theta: 0 }]
  const rings = [
    { r: 0.25, n: 8 },
    { r: 0.5, n: 12 },
    { r: 0.72, n: 14 },
    { r: 0.92, n: 14 },
  ]
  for (const ring of rings) {
    for (let i = 0; i < ring.n; i++) {
      sites.push({ r: ring.r, theta: (i / ring.n) * Math.PI * 2 + ring.r })
    }
  }
  return sites
}

const SITES = samplingSites()

export class WaferMap {
  readonly canvas: HTMLCanvasElement
  private readonly ctx: CanvasRenderingContext2D
  /** Offscreen buffer for the field, so the 3D texture can reuse it directly. */
  readonly fieldCanvas: HTMLCanvasElement
  private readonly fieldCtx: CanvasRenderingContext2D

  /** Layout happens in this coordinate space; the bitmap is scaled for DPR. */
  private readonly logicalSize: number

  constructor(size = 320) {
    this.logicalSize = size
    this.canvas = document.createElement('canvas')
    const dpr = Math.min(window.devicePixelRatio || 1, 3)
    this.canvas.width = Math.round(size * dpr)
    this.canvas.height = Math.round((size + 44) * dpr)
    this.canvas.style.width = size + 'px'
    this.canvas.style.maxWidth = '100%'
    this.ctx = this.canvas.getContext('2d')!
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

    this.fieldCanvas = document.createElement('canvas')
    this.fieldCanvas.width = this.fieldCanvas.height = 256
    this.fieldCtx = this.fieldCanvas.getContext('2d')!
  }

  /**
   * Draw the radial field into the square offscreen buffer. This one is the
   * texture that goes onto the wafer in the 3D chamber, so it is pure field -
   * no axes, no labels, edge-to-edge.
   */
  private renderField(opts: WaferMapOptions): { lo: number; hi: number; avg: number } {
    const { values, mode } = opts
    const n = values.length
    const avg = mean(values)

    let lo = Infinity
    let hi = -Infinity
    for (let i = 0; i < n; i++) {
      if (values[i] < lo) lo = values[i]
      if (values[i] > hi) hi = values[i]
    }
    if (!Number.isFinite(lo)) {
      lo = 0
      hi = 1
    }

    const size = this.fieldCanvas.width
    const img = this.fieldCtx.createImageData(size, size)
    const data = img.data
    const c = size / 2
    // Deviation is symmetric about the mean, so both arms share one scale.
    const spread = Math.max(Math.abs(hi - avg), Math.abs(avg - lo), 1e-30)

    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const dx = (x - c) / c
        const dy = (y - c) / c
        const rr = Math.hypot(dx, dy)
        const o = (y * size + x) * 4

        if (rr > 1.0) {
          data[o + 3] = 0
          continue
        }

        // Nearest-sample lookup with linear interpolation between radial bins.
        const fi = clamp(rr, 0, 0.9999) * (n - 1)
        const i0 = Math.floor(fi)
        const i1 = Math.min(i0 + 1, n - 1)
        const f = fi - i0
        const v = values[i0] * (1 - f) + values[i1] * f

        let colour: string
        if (mode === 'deviation') {
          colour = diverging((v - avg) / spread)
        } else {
          colour = sequential(hi > lo ? (v - lo) / (hi - lo) : 0.5)
        }

        const m = colour.startsWith('#')
          ? [
              parseInt(colour.slice(1, 3), 16),
              parseInt(colour.slice(3, 5), 16),
              parseInt(colour.slice(5, 7), 16),
            ]
          : colour.slice(4, -1).split(',').map(Number)

        data[o] = m[0]
        data[o + 1] = m[1]
        data[o + 2] = m[2]
        // Feather the last pixel of the edge so the disc does not alias.
        data[o + 3] = rr > 0.985 ? Math.round(255 * (1 - (rr - 0.985) / 0.015)) : 255
      }
    }

    this.fieldCtx.clearRect(0, 0, size, size)
    this.fieldCtx.putImageData(img, 0, 0)

    // Notch at the bottom - the orientation feature on a 300 mm wafer.
    this.fieldCtx.globalCompositeOperation = 'destination-out'
    this.fieldCtx.beginPath()
    this.fieldCtx.arc(c, size - 2, size * 0.045, 0, Math.PI * 2)
    this.fieldCtx.fill()
    this.fieldCtx.globalCompositeOperation = 'source-over'

    return { lo, hi, avg }
  }

  /** Draw the full labelled map for the panel. */
  render(opts: WaferMapOptions): HTMLCanvasElement {
    const { lo, hi, avg } = this.renderField(opts)
    const ctx = this.ctx
    const W = this.logicalSize
    const size = this.logicalSize

    ctx.clearRect(0, 0, size, size + 44)
    ctx.fillStyle = SURFACE
    ctx.fillRect(0, 0, size, size + 44)

    // Wafer field.
    ctx.drawImage(this.fieldCanvas, 8, 8, size - 16, size - 16)

    // Hairline ring so the disc has an edge against the surface.
    ctx.strokeStyle = INK.border
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.arc(W / 2, W / 2, (size - 16) / 2, 0, Math.PI * 2)
    ctx.stroke()

    if (opts.showSites !== false) {
      const c = W / 2
      const R = (size - 16) / 2
      ctx.fillStyle = 'rgba(255,255,255,0.5)'
      for (const s of SITES) {
        ctx.beginPath()
        ctx.arc(c + Math.cos(s.theta) * s.r * R, c + Math.sin(s.theta) * s.r * R, 1.4, 0, Math.PI * 2)
        ctx.fill()
      }
    }

    // Colour bar.
    const barY = size + 6
    const barX = 12
    const barW = W - 90
    const grad = ctx.createLinearGradient(barX, 0, barX + barW, 0)
    for (let i = 0; i <= 20; i++) {
      const t = i / 20
      grad.addColorStop(
        t,
        opts.mode === 'deviation' ? diverging(t * 2 - 1) : sequential(t),
      )
    }
    ctx.fillStyle = grad
    ctx.fillRect(barX, barY, barW, 8)
    ctx.strokeStyle = INK.border
    ctx.strokeRect(barX + 0.5, barY + 0.5, barW - 1, 7)

    ctx.font = `10px ${FONT_STACK}`
    ctx.fillStyle = INK.muted
    ctx.textBaseline = 'top'
    const fmt = (v: number) => (Math.abs(v) >= 100 ? v.toFixed(0) : v.toFixed(1))
    if (opts.mode === 'deviation') {
      const spread = Math.max(Math.abs(hi - avg), Math.abs(avg - lo))
      ctx.textAlign = 'left'
      ctx.fillText(`-${fmt(spread)}`, barX, barY + 12)
      ctx.textAlign = 'center'
      ctx.fillText(tOr('tool.panel.ValueOnTarget', 'on target'), barX + barW / 2, barY + 12)
      ctx.textAlign = 'right'
      ctx.fillText(`+${fmt(spread)}`, barX + barW, barY + 12)
    } else {
      ctx.textAlign = 'left'
      ctx.fillText(fmt(lo), barX, barY + 12)
      ctx.textAlign = 'right'
      ctx.fillText(fmt(hi), barX + barW, barY + 12)
    }

    ctx.textAlign = 'left'
    ctx.fillStyle = INK.secondary
    ctx.fillText(opts.unit, barX + barW + 8, barY)

    if (opts.caption) {
      ctx.fillStyle = INK.muted
      ctx.font = `10px ${FONT_STACK}`
      ctx.textAlign = 'center'
      ctx.fillText(opts.caption, W / 2, barY + 24)
    }

    return this.canvas
  }
}
